/** Largest text file the worker returns for preview. */
export const MAX_FILE_PREVIEW_BYTES = 1_000_000;

/** Identifies a text file's content for the editor's save: lowercase hex
 * SHA-256 of its UTF-8 bytes. A client hashes the text it began editing and
 * the worker hashes the file on disk, so a save can tell whether the file
 * changed underneath the edit. Mirrored by the native `FileEditing.hash`. */
export async function fileContentHash(content: string | Uint8Array): Promise<string> {
  const bytes = typeof content === "string" ? new TextEncoder().encode(content) : content;
  const digest = await crypto.subtle.digest("SHA-256", bytes as Uint8Array<ArrayBuffer>);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Largest image the worker returns for preview. Screenshots routinely pass the
 * text limit, and base64 of this still fits comfortably in one worker frame. */
export const MAX_IMAGE_PREVIEW_BYTES = 5_000_000;

/** How many leading bytes `sniffImageMediaType` needs. */
export const IMAGE_SNIFF_BYTES = 16;

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "ico", "avif"]);

/** Whether `path` names a raster image a preview can show. Decides only whether
 * one is worth asking for: the bytes themselves decide what the file is. */
export function isPreviewableImagePath(path: string): boolean {
  const dot = path.lastIndexOf(".");
  return dot > path.lastIndexOf("/") && IMAGE_EXTENSIONS.has(path.slice(dot + 1).toLowerCase());
}

/** The media type of a raster image every client can decode, from its leading
 * bytes; undefined for anything else. Mirrored by the native `ImagePreviewSniff`. */
export function sniffImageMediaType(bytes: Uint8Array): string | undefined {
  const at = (offset: number, ...values: number[]) => values.every((value, index) => bytes[offset + index] === value);
  const ascii = (offset: number, text: string) => at(offset, ...Array.from(text, (char) => char.charCodeAt(0)));
  if (at(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  if (at(0, 0xff, 0xd8, 0xff)) return "image/jpeg";
  if (ascii(0, "GIF87a") || ascii(0, "GIF89a")) return "image/gif";
  if (ascii(0, "RIFF") && ascii(8, "WEBP")) return "image/webp";
  // "BM" alone begins plenty of text; the two reserved header words are zero.
  if (ascii(0, "BM") && at(6, 0, 0, 0, 0)) return "image/bmp";
  if (at(0, 0, 0, 1, 0)) return "image/x-icon";
  if (ascii(4, "ftyp") && (ascii(8, "avif") || ascii(8, "avis"))) return "image/avif";
  return undefined;
}
